<template>
  <div class="form-builder">
    <div class="vx-row">
      <!-- Left Column - Builder -->
      <div class="vx-col lg:w-1/2 w-full">
        <vx-card title="Form Builder" subtitle="Create dynamic forms with customizable fields">
          
          <!-- Loading State -->
          <div v-if="loading" class="loading-overlay">
            <vs-loading>Loading form...</vs-loading>
          </div>
          
          <!-- Tabs -->
          <vs-tabs>
            <!-- Configuration Tab -->
            <vs-tab label="Configuration" icon="settings">
              <div class="form-config-section">
                <h3 class="text-lg font-semibold mb-4">Form Configuration</h3>
                
                <div class="vx-row mb-4">
                  <div class="vx-col w-full">
                    <vs-input 
                      v-model="formConfig.title" 
                      label-placeholder="Form Title" 
                      class="w-full" 
                      placeholder="Enter form title" />
                  </div>
                </div>
                
                <div class="vx-row mb-4">
                  <div class="vx-col w-full">
                    <vs-input 
                      v-model="formConfig.name" 
                      label-placeholder="Form Name (Internal)" 
                      class="w-full" 
                      placeholder="Enter internal form name" />
                    <small class="text-gray-500 mt-1">Internal name for administrative purposes</small>
                  </div>
                </div>
                
                <div class="vx-row mb-4">
                  <div class="vx-col w-full">
                    <vs-textarea 
                      v-model="formConfig.summary" 
                      label-placeholder="Form Summary" 
                      class="w-full" 
                      placeholder="Enter form description or summary"
                      rows="3" />
                  </div>
                </div>
              </div>
            </vs-tab>

            <!-- Fields Tab -->
            <vs-tab label="Fields" icon="list">
              <div class="field-management-section">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-semibold">Form Fields</h3>
                  <vs-button @click="addField" color="primary" size="small">
                    <i class="fas fa-plus mr-2"></i>
                    Add Field
                  </vs-button>
                </div>

                <!-- Fields List -->
                <div class="fields-list">
                  <div 
                    v-for="(field, index) in formConfig.fields" 
                    :key="field.id"
                    class="field-item">
                    
                    <div class="field-content">
                      <div class="field-row">
                        <div class="field-handle">
                          <i class="fas fa-grip-vertical text-gray-400 cursor-move"></i>
                        </div>
                        
                        <div class="field-input">
                          <vs-input 
                            v-model="field.title" 
                            label-placeholder="Field Title" 
                            class="w-full" 
                            placeholder="Enter field title" />
                        </div>
                        
                        <div class="field-type">
                          <vs-select 
                            v-model="field.type" 
                            label="Field Type"
                            class="w-full">
                            <vs-select-item key="text" value="text" text="Text" />
                            <vs-select-item key="yesno" value="yesno" text="Yes/No" />
                            <vs-select-item key="datetime" value="datetime" text="Date Time" />
                            <vs-select-item key="dropdown" value="dropdown" text="Dropdown" />
                            <vs-select-item key="email" value="email" text="Email" />
                            <vs-select-item key="file" value="file" text="File Upload" />
                            <vs-select-item key="process-identifier" value="process-identifier" text="Process Identifier" />
                          </vs-select>
                        </div>
                        
                        
                        
                        <div class="field-actions">
                          <vs-dropdown>
                            <vs-button 
                              color="primary" 
                              type="border" 
                              size="small">
                              <i class="fas fa-ellipsis-v"></i>
                            </vs-button>
                            
                            <vs-dropdown-menu>
                              <vs-dropdown-item 
                                @click="moveFieldUp(index)" 
                                :disabled="index === 0">
                                <i class="fas fa-arrow-up mr-2"></i>
                                Move Up
                              </vs-dropdown-item>
                              
                              <vs-dropdown-item 
                                @click="moveFieldDown(index)" 
                                :disabled="index === formConfig.fields.length - 1">
                                <i class="fas fa-arrow-down mr-2"></i>
                                Move Down
                              </vs-dropdown-item>
                              
                              <vs-dropdown-item 
                                @click="removeField(index)" 
                                class="text-danger">
                                <i class="fas fa-trash mr-2"></i>
                                Delete
                              </vs-dropdown-item>
                            </vs-dropdown-menu>
                          </vs-dropdown>
                        </div>
                      </div>
                      
                      <div v-if="field.type === 'dropdown'" class="field-options">
                        <vs-button 
                          @click="editDropdownValues(index)"
                          color="primary" 
                          type="border" 
                          size="small">
                          <i class="fas fa-edit mr-1"></i>
                          Edit Values
                        </vs-button>
                        <span v-if="field.options && field.options.length > 0" class="text-xs text-gray-500 ml-2">
                          {{ field.options.length }} option(s)
                        </span>
                      </div>
                      
                      <div class="field-process-identifier">
                          <vs-checkbox 
                            v-model="field.isProcessIdentifier"
                            color="primary">
                            Process Identifier
                          </vs-checkbox>
                          <small class="text-gray-500 ml-2">This field will be used to identify the process run</small>
                        </div>
                    </div>
                  </div>
                  
                  <!-- Empty State -->
                  <div v-if="formConfig.fields.length === 0" class="empty-state">
                    <div class="text-center py-6 text-gray-500">
                      <i class="fas fa-plus-circle text-3xl mb-3"></i>
                      <p class="text-base">No fields added yet</p>
                      <p class="text-sm">Click "Add Field" to start building your form</p>
                    </div>
                  </div>
                </div>
              </div>
            </vs-tab>
          </vs-tabs>

          <!-- Actions -->
          <div class="form-actions">
            <div class="flex justify-end space-x-2">
              <vs-button @click="resetForm" color="warning" type="border">
                Reset Form
              </vs-button>
              <vs-button @click="saveForm" color="success" :disabled="saving">
                <vs-loading v-if="saving" />
                {{ saving ? 'Saving...' : 'Save Form' }}
              </vs-button>
              <vs-button @click="exportForm" color="primary" type="border">
                Export Form
              </vs-button>
            </div>
          </div>

        </vx-card>
      </div>

      <!-- Right Column - Preview -->
      <div class="vx-col lg:w-1/2 w-full">
        <vx-card title="Form Preview" subtitle="Live preview of your form">
          
          <div class="preview-container">
            <div class="preview-form">
              <div v-if="formConfig.title" class="form-title mb-4">
                <h2 class="text-2xl font-bold">{{ formConfig.title }}</h2>
              </div>
              
              <div v-if="formConfig.summary" class="form-summary mb-6">
                <p class="text-gray-600">{{ formConfig.summary }}</p>
              </div>
              
              <!-- Form Fields Preview -->
              <div class="form-fields">
                <div 
                  v-for="(field, index) in formConfig.fields" 
                  :key="field.id"
                  class="vx-row mb-2">
                  <div class="vx-col w-full">
                    <!-- Text Field -->
                    <vs-input 
                      v-if="field.type === 'text' || !field.type"
                      :label-placeholder="field.title || 'Field Title'" 
                      class="w-full" 
                      :placeholder="`Enter ${field.title || 'field value'}`" />
                    
                    <!-- Yes/No Field -->
                    <div v-else-if="field.type === 'yesno'" class="checkbox-field">
                      <vs-checkbox 
                        :label="field.title || 'Field Title'"
                        class="w-full" />
                    </div>
                    
                    <!-- DateTime Field -->
                    <div v-else-if="field.type === 'datetime'" class="datetime-field">
                      <vs-input 
                        :label-placeholder="field.title || 'Field Title'" 
                        class="w-full" 
                        type="datetime-local"
                        placeholder="Select date and time" />
                    </div>
                    
                    <!-- Dropdown Field -->
                    <div v-else-if="field.type === 'dropdown'" class="dropdown-field">
                      <vs-select 
                        :label="field.title || 'Field Title'"
                        class="w-full"
                        placeholder="Select an option">
                        <vs-select-item 
                          v-for="option in field.options" 
                          :key="option.value"
                          :value="option.value" 
                          :text="option.text" />
                      </vs-select>
                    </div>
                    
                    <!-- Email Field -->
                    <vs-input 
                      v-else-if="field.type === 'email'"
                      :label-placeholder="field.title || 'Field Title'" 
                      class="w-full" 
                      type="email"
                      :placeholder="`Enter ${field.title || 'email address'}`" />
                    
                    <!-- File Upload Field -->
                    <div v-else-if="field.type === 'file'" class="file-field">
                      <vs-input 
                        :label-placeholder="field.title || 'Field Title'" 
                        class="w-full" 
                        type="file"
                        disabled />
                      <small class="text-gray-500 mt-1">Coming soon</small>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- Submit Button -->
              <div class="vx-row mt-6" v-if="formConfig.fields.length > 0">
                <div class="vx-col w-full">
                  <vs-button class="mr-3 mb-2">Submit</vs-button>
                  <vs-button color="warning" type="border" class="mb-2">Reset</vs-button>
                </div>
              </div>

              <!-- Empty Preview State -->
              <div v-if="formConfig.fields.length === 0" class="empty-preview">
                <div class="text-center py-12 text-gray-400">
                  <i class="fas fa-eye text-4xl mb-4"></i>
                  <p class="text-lg">Form preview will appear here</p>
                  <p class="text-sm">Add fields to see your form</p>
                </div>
              </div>
            </div>
          </div>

        </vx-card>
      </div>
    </div>

    <!-- Dropdown Values Editor Modal -->
    <vs-popup title="Edit Dropdown Values" :active.sync="showDropdownEditor">
      <div class="dropdown-editor">
        <div class="mb-4">
          <h4 class="text-lg font-semibold mb-2">Field: {{ (currentEditingField && currentEditingField.title) || 'Untitled Field' }}</h4>
          <p class="text-gray-600 text-sm">Add options for this dropdown field</p>
        </div>

        <div class="options-list mb-4">
          <div 
            v-for="(option, index) in currentDropdownOptions" 
            :key="index"
            class="option-item flex items-center mb-2">
            <vs-input 
              v-model="option.text" 
              placeholder="Option text"
              class="flex-1 mr-2" />
            <vs-input 
              v-model="option.value" 
              placeholder="Option value"
              class="flex-1 mr-2" />
            <vs-button 
              @click="removeDropdownOption(index)"
              color="danger" 
              type="border" 
              size="small">
              <i class="fas fa-trash"></i>
            </vs-button>
          </div>
        </div>

        <div class="add-option mb-4">
          <vs-button 
            @click="addDropdownOption"
            color="success" 
            type="border" 
            size="small">
            <i class="fas fa-plus mr-1"></i>
            Add Option
          </vs-button>
        </div>

        <div class="modal-actions">
          <vs-button @click="saveDropdownOptions" color="primary">
            Save Options
          </vs-button>
          <vs-button @click="cancelDropdownEdit" color="warning" type="border">
            Cancel
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import VxCard from '@/components/vx-card/VxCard.vue'

export default {
  name: 'FormBuilder',
  components: {
    VxCard
  },
  data() {
    return {
      formConfig: {
        id: null,
        title: '',
        name: '',
        summary: '',
        fields: []
      },
      nextFieldId: 1,
      loading: false,
      saving: false,
      showDropdownEditor: false,
      currentEditingField: null,
      currentEditingFieldIndex: -1,
      currentDropdownOptions: []
    }
  },
  async mounted() {
    await this.loadFormFromQuery();
  },
  methods: {
    async loadFormFromQuery() {
      const formId = this.$route.query.formid;
      if (formId) {
        this.loading = true;
        try {
          const query = `
            query FormById($id: Int!) {
              formById(id: $id) {
                id
                title
                name
                formMetadata
                createdAt
                updatedAt
              }
            }
          `;

          const response = await TCVelikClient.PostRelative('/api/graphql', {
            query: query,
            variables: { id: parseInt(formId) }
          });

          if (response && response.data && response.data.data && response.data.data.formById) {
            const form = response.data.data.formById;
            this.formConfig = {
              id: form.id,
              title: form.title,
              name: form.name || '',
              summary: '',
              fields: []
            };
            
            // Parse form metadata if it exists
            if (form.formMetadata) {
              try {
                const metadata = JSON.parse(form.formMetadata);
                if (metadata.summary) {
                  this.formConfig.summary = metadata.summary;
                }
                if (metadata.fields && Array.isArray(metadata.fields)) {
                  this.formConfig.fields = metadata.fields;
                  // Update nextFieldId to avoid conflicts
                  if (metadata.fields.length > 0) {
                    this.nextFieldId = Math.max(...metadata.fields.map(f => f.id)) + 1;
                  }
                }
              } catch (error) {
                console.error('Error parsing form metadata:', error);
              }
            }
            
            this.$vs.notify({
              title: 'Form Loaded',
              text: `Loaded form: ${form.title}`,
              color: 'success'
            });
          }
        } catch (error) {
          console.error('Error loading form:', error);
          this.$vs.notify({
            title: 'Error',
            text: 'Failed to load form',
            color: 'danger'
          });
        } finally {
          this.loading = false;
        }
      }
    },
    addField() {
      const newField = {
        id: this.nextFieldId++,
        title: '',
        type: 'text',
        options: [], // For dropdown fields
        isProcessIdentifier: false // For process identifier flag
      }
      this.formConfig.fields.push(newField)
    },
    
    removeField(index) {
      this.formConfig.fields.splice(index, 1)
    },
    
    moveFieldUp(index) {
      if (index > 0) {
        const field = this.formConfig.fields.splice(index, 1)[0]
        this.formConfig.fields.splice(index - 1, 0, field)
      }
    },
    
    moveFieldDown(index) {
      if (index < this.formConfig.fields.length - 1) {
        const field = this.formConfig.fields.splice(index, 1)[0]
        this.formConfig.fields.splice(index + 1, 0, field)
      }
    },
    
    resetForm() {
      this.formConfig = {
        id: null,
        title: '',
        name: '',
        summary: '',
        fields: []
      }
      this.nextFieldId = 1
      this.$vs.notify({
        title: 'Form Reset',
        text: 'Form has been reset to default state',
        color: 'warning'
      })
    },
    
    async saveForm() {
      if (!this.formConfig.title) {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please enter a form title',
          color: 'danger'
        })
        return
      }
      
      if (this.formConfig.fields.length === 0) {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please add at least one field',
          color: 'danger'
        })
        return
      }
      
      this.saving = true
      try {
        const formMetadata = JSON.stringify({
          summary: this.formConfig.summary,
          fields: this.formConfig.fields
        })
        
        let mutation, variables
        if (this.formConfig.id) {
          // Update existing form
          mutation = `
            mutation UpdateForm($id: Int!, $title: String!, $name: String, $formMetadata: String!) {
              updateForm(id: $id, title: $title, name: $name, formMetadata: $formMetadata) {
                id
                title
                name
                formMetadata
                updatedAt
              }
            }
          `
          variables = {
            id: this.formConfig.id,
            title: this.formConfig.title,
            name: this.formConfig.name || null,
            formMetadata: formMetadata
          }
        } else {
          // Create new form
          mutation = `
            mutation CreateForm($title: String!, $name: String, $formMetadata: String!) {
              createForm(title: $title, name: $name, formMetadata: $formMetadata) {
                id
                title
                name
                formMetadata
                createdAt
              }
            }
          `
          variables = {
            title: this.formConfig.title,
            name: this.formConfig.name || null,
            formMetadata: formMetadata
          }
        }
        
        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: mutation,
          variables: variables
        })
        
        if (response && response.data && response.data.data) {
          const formData = this.formConfig.id 
            ? response.data.data.updateForm 
            : response.data.data.createForm
          
          if (formData) {
            this.formConfig.id = formData.id
            this.$vs.notify({
              title: 'Success',
              text: this.formConfig.id ? 'Form updated successfully' : 'Form created successfully',
              color: 'success'
            })
          }
        }
      } catch (error) {
        console.error('Error saving form:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to save form',
          color: 'danger'
        })
      } finally {
        this.saving = false
      }
    },
    
    exportForm() {
      if (!this.formConfig.title) {
        this.$vs.notify({
          title: 'Validation Error',
          text: 'Please enter a form title before exporting',
          color: 'danger'
        })
        return
      }
      
      const formData = {
        ...this.formConfig,
        exportedAt: new Date().toISOString(),
        version: '1.0'
      }
      
      const dataStr = JSON.stringify(formData, null, 2)
      const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr)
      
      const exportFileDefaultName = `${this.formConfig.title.replace(/\s+/g, '_')}_form.json`
      
      const linkElement = document.createElement('a')
      linkElement.setAttribute('href', dataUri)
      linkElement.setAttribute('download', exportFileDefaultName)
      linkElement.click()
      
      this.$vs.notify({
        title: 'Export Complete',
        text: 'Form exported successfully',
        color: 'success'
      })
    },

    editDropdownValues(fieldIndex) {
      const field = this.formConfig.fields[fieldIndex]
      this.currentEditingField = field
      this.currentEditingFieldIndex = fieldIndex
      this.currentDropdownOptions = field.options ? [...field.options] : []
      this.showDropdownEditor = true
    },

    addDropdownOption() {
      this.currentDropdownOptions.push({
        text: '',
        value: ''
      })
    },

    removeDropdownOption(index) {
      this.currentDropdownOptions.splice(index, 1)
    },

    saveDropdownOptions() {
      if (this.currentEditingFieldIndex >= 0) {
        this.formConfig.fields[this.currentEditingFieldIndex].options = [...this.currentDropdownOptions]
      }
      this.showDropdownEditor = false
      this.currentEditingField = null
      this.currentEditingFieldIndex = -1
      this.currentDropdownOptions = []
    },

    cancelDropdownEdit() {
      this.showDropdownEditor = false
      this.currentEditingField = null
      this.currentEditingFieldIndex = -1
      this.currentDropdownOptions = []
    }
  }
}
</script>

<style scoped>
.form-builder {
  height: 100vh;
  overflow-y: auto;
}

.form-config-section {
  padding: 1.25rem;
}

.field-management-section {
  padding: 1.25rem;
}

.field-item {
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background-color: white;
  transition: box-shadow 0.15s ease-in-out;
}

.field-item:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.field-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.field-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.field-handle {
  display: flex;
  align-items: center;
  cursor: move;
}

.field-input {
  flex: 1;
}

.field-actions {
  display: flex;
  align-items: center;
}

.empty-state {
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}

.preview-container {
  background-color: white;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  padding: 1.5rem;
  min-height: 400px;
}

.form-title h2 {
  color: #1f2937;
}

.form-summary p {
  color: #6b7280;
}

.form-actions {
  border-top: 1px solid #e5e7eb;
  padding-top: 1rem;
}

.empty-preview {
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}

/* Field type specific styles */
.field-type {
  min-width: 150px;
}

.field-process-identifier {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: #f0f9ff;
  border: 1px solid #0ea5e9;
  border-radius: 0.375rem;
}

.field-process-identifier small {
  color: #0369a1;
  font-size: 0.75rem;
}

.field-options {
  padding-left: 2rem;
  border-left: 2px solid #e5e7eb;
  margin-left: 1rem;
}

.dropdown-editor {
  min-width: 500px;
}

.option-item {
  padding: 0.5rem;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  background-color: #f9fafb;
}

.modal-actions {
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .form-builder {
    height: auto;
  }
  
  .preview-container {
    min-height: 300px;
  }
}
</style>
